import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Loader2, Truck, ArrowRight, User, UserCheck, ExternalLink, Download, FileText, X, Filter } from 'lucide-react';
import { api, API_BASE_URL, tokenStore, downloadBlob } from '@/api/client';
import { STATUS_LABEL, STATUS_COLOR } from './constants';
import { BRAND } from '@/brand';

interface OfdOrder {
  id: string;
  orderType: 'B2C' | 'B2B';
  orderNo?: string;
  systemOrderNo?: string;
  invoiceNo?: string | null;
  awbNo?: string | null;
  lrNo?: string | null;
  customerName?: string;
  customerMobile?: string;
  statusId: number;
  bookingDate?: string;
  createdAt?: string;
  courier?: { id: number; name: string; code: string };
  createdByName?: string | null;
  assignedToName?: string | null;
  assignedAt?: string | null;
}
type Opt = { id: number; name: string };
interface Filters { fromUsers: Opt[]; assignedUsers: Opt[]; merchants: Opt[]; couriers: Opt[] }

// Date only. This screen and its export are worked off a printed round sheet, where the time a
// parcel was booked is noise — and the export drops it too, so the two now agree.
const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const NO_DL_STATUSES = [9, 21, 24];

export function InhouseOfdOrdersPage() {
  // '' = B2C and B2B in one list, which is how a delivery round actually works: a rider leaves with
  // both on the same trip, so splitting them across two tabs meant two sheets for one van.
  const [orderType, setOrderType] = useState<'' | 'B2C' | 'B2B'>('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyManifest, setBusyManifest] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Applied filters (only change when the user hits Search/selects).
  const [f, setF] = useState({ search: '', fromUserId: '', assignedUserId: '', merchantId: '', courierId: '', startDate: '', endDate: '' });
  const setFilter = (patch: Partial<typeof f>) => { setF((p) => ({ ...p, ...patch })); setPage(1); setSelected(new Set()); };

  const { data: filters } = useQuery({
    queryKey: ['inhouse-ofd-filters', orderType],
    queryFn: async () => (await api.get<Filters>('/orders/inhouse-ofd/filters', { params: { orderType } })).data,
    staleTime: 60 * 1000,
  });

  const params = useMemo(() => ({
    ...(orderType ? { orderType } : {}),   // omitted ⇒ the server returns both services
    ...(f.search ? { search: f.search } : {}),
    ...(f.fromUserId ? { createdById: f.fromUserId } : {}),
    ...(f.assignedUserId ? { assignedUserId: f.assignedUserId } : {}),
    ...(f.merchantId ? { merchantId: f.merchantId } : {}),
    ...(f.courierId ? { courierId: f.courierId } : {}),
    ...(f.startDate ? { startDate: f.startDate } : {}),
    ...(f.endDate ? { endDate: f.endDate } : {}),
  }), [orderType, f]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['inhouse-ofd', params, page],
    queryFn: async () => (await api.get<{ items: OfdOrder[]; total: number }>('/orders/inhouse-ofd', { params: { ...params, page, pageSize } })).data,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const anyFilter = !!(f.search || f.fromUserId || f.assignedUserId || f.merchantId || f.courierId || f.startDate || f.endDate);

  const switchType = (t: '' | 'B2C' | 'B2B') => { setOrderType(t); setPage(1); setSelected(new Set()); };
  // 8 fixed columns, plus Type when both services share the table.
  const colCount = orderType ? 8 : 9;
  const clearFilters = () => { setSearchInput(''); setF({ search: '', fromUserId: '', assignedUserId: '', merchantId: '', courierId: '', startDate: '', endDate: '' }); setPage(1); setSelected(new Set()); };

  const toggleOne = (id: string) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectableIds = rows.filter((o) => (o.awbNo || o.lrNo) && !NO_DL_STATUSES.includes(o.statusId)).map((o) => o.id);
  const toggleAll = () => setSelected((p) => (selectableIds.every((id) => p.has(id)) ? new Set() : new Set(selectableIds)));

  const runExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params as any).toString();
      await downloadBlob(`/orders/inhouse-ofd/export?${qs}`, `inhouse-ofd-${orderType || 'all'}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch { toast.error('Export failed'); } finally { setExporting(false); }
  };

  const downloadManifest = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error('Select orders first'); return; }
    if (ids.length > 250) { toast.error('Maximum 250 orders at a time'); return; }
    setBusyManifest(true);
    try {
      const r = await fetch(`${API_BASE_URL}/orders/delivery-sheet`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokenStore.get()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderIds: ids }),
      });
      if (!r.ok) { toast.error(`Manifest failed (${r.status})`); return; }
      const html = await r.text();
      const w = window.open('', '_blank');
      if (!w) { toast.error('Pop-up blocked — allow pop-ups'); return; }
      w.document.open(); w.document.write(html); w.document.close();
    } catch { toast.error('Manifest failed'); } finally { setBusyManifest(false); }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Truck size={22} /> In-House OFD Orders</h1>
          <p className="text-sm text-slate-500">Out-for-delivery orders on the in-house <b>{BRAND.name}</b> courier, with who booked them and who they're out with.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={downloadManifest} disabled={busyManifest || selected.size === 0}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
            {busyManifest ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Download Delivery Sheet{selected.size > 0 ? ` (${selected.size})` : ''}
          </button>
          <button onClick={runExport} disabled={exporting}
            className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export
          </button>
        </div>
      </div>

      {/* Order type + search */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
          {([['', 'All'], ['B2C', 'B2C'], ['B2B', 'B2B']] as const).map(([t, label], i) => (
            <button key={label} onClick={() => switchType(t)}
              className={`px-4 py-1.5 font-medium ${orderType === t ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'} ${i > 0 ? 'border-l border-slate-200' : ''}`}>{label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setFilter({ search: searchInput.trim() })}
            placeholder={orderType === 'B2C' ? 'Search AWB / order / customer…'
              : orderType === 'B2B' ? 'Search LR / invoice / customer…'
              : 'Search AWB / LR / order / customer…'} className="input w-64" />
          <button onClick={() => setFilter({ search: searchInput.trim() })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1 hover:bg-slate-50"><Search size={14} /> Search</button>
        </div>
        <div className="ml-auto text-sm text-slate-500">{total} order{total === 1 ? '' : 's'}</div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap items-end gap-3">
        <FilterSelect label="From user (booked by)" value={f.fromUserId} onChange={(v) => setFilter({ fromUserId: v })} options={filters?.fromUsers} icon={<User size={12} />} />
        <FilterSelect label="Assigned user (to)" value={f.assignedUserId} onChange={(v) => setFilter({ assignedUserId: v })} options={filters?.assignedUsers} icon={<UserCheck size={12} />} />
        <FilterSelect label="Merchant" value={f.merchantId} onChange={(v) => setFilter({ merchantId: v })} options={filters?.merchants} />
        <FilterSelect label="Courier" value={f.courierId} onChange={(v) => setFilter({ courierId: v })} options={filters?.couriers} />
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">From date</label>
          <input type="date" value={f.startDate} onChange={(e) => setFilter({ startDate: e.target.value })} className="input" />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">To date</label>
          <input type="date" value={f.endDate} onChange={(e) => setFilter({ endDate: e.target.value })} className="input" />
        </div>
        {anyFilter && (
          <button onClick={clearFilters} className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"><X size={14} /> Clear</button>
        )}
        <span className="text-xs text-slate-400 flex items-center gap-1 ml-auto"><Filter size={12} /> Filters apply to the list, export &amp; manifest</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" checked={selectableIds.length > 0 && selectableIds.every((id) => selected.has(id))} onChange={toggleAll} title="Select all shippable on this page" /></th>
                {!orderType && <th className="text-left px-4 py-3">Type</th>}
                <th className="text-left px-4 py-3">{orderType === 'B2B' ? 'Invoice / LR' : 'Order'}</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Tracking</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3"><span className="inline-flex items-center gap-1"><User size={12} /> From (booked by)</span></th>
                <th className="text-left px-4 py-3"><span className="inline-flex items-center gap-1"><UserCheck size={12} /> To (assigned)</span></th>
                <th className="text-right px-4 py-3">Open</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (<tr><td colSpan={colCount} className="text-center py-12 text-slate-400"><Loader2 size={20} className="mx-auto animate-spin" /></td></tr>)}
              {!isLoading && rows.length === 0 && (<tr><td colSpan={colCount} className="text-center py-12 text-slate-400">No {BRAND.name} out-for-delivery {orderType || 'B2C / B2B'} orders{anyFilter ? ' for these filters' : ''}.</td></tr>)}
              {rows.map((o) => {
                const canSelect = (o.awbNo || o.lrNo) && !NO_DL_STATUSES.includes(o.statusId);
                return (
                  <tr key={o.id} className={`border-t border-slate-100 hover:bg-slate-50/60 align-top ${selected.has(o.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-3 py-3 text-center">
                      {canSelect && <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} />}
                    </td>
                    {!orderType && (
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium border ${o.orderType === 'B2B' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>{o.orderType}</span>
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{o.orderType === 'B2B' ? (o.invoiceNo || o.orderNo || '—') : (o.orderNo ?? o.systemOrderNo ?? '—')}</div>
                      {o.systemOrderNo && o.systemOrderNo !== o.orderNo && <div className="text-xs text-slate-400 font-mono">{o.systemOrderNo}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800">{o.customerName ?? '—'}</div>
                      {o.customerMobile && <div className="text-xs text-slate-400">{o.customerMobile}</div>}
                    </td>
                    {/* LR first on a B2B consignment — that is the number the courier and the customer
                        quote. `awbNo || lrNo` printed the wrong one, harmless while the page was
                        pinned to one service and wrong the moment both share a table. */}
                    <td className="px-4 py-3 font-mono text-xs">{(o.orderType === 'B2B' ? (o.lrNo || o.awbNo) : (o.awbNo || o.lrNo)) || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLOR[o.statusId] ?? 'bg-slate-100 text-slate-600'}`}>{STATUS_LABEL[o.statusId] ?? `#${o.statusId}`}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800">{o.createdByName ?? '—'}</div>
                      <div className="text-xs text-slate-400">{fmt(o.bookingDate ?? o.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      {o.assignedToName ? (
                        <>
                          <div className="inline-flex items-center gap-1 text-slate-800"><ArrowRight size={12} className="text-emerald-500" /> {o.assignedToName}</div>
                          <div className="text-xs text-slate-400">{fmt(o.assignedAt)}</div>
                        </>
                      ) : <span className="text-xs text-amber-500">Not assigned yet</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Route from the ROW: with both services listed, the page-level type sent
                          every B2B consignment to the B2C detail route. */}
                      <Link to={`/orders-${o.orderType.toLowerCase()}/${o.id}?back=/inhouse-ofd`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs hover:bg-slate-50">
                        <ExternalLink size={13} /> View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm">
          <span className="text-slate-500">{isFetching ? 'Loading…' : `Page ${page} of ${lastPage}`}</span>
          <div className="flex gap-1">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-40">Prev</button>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border border-slate-200 rounded-lg disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, icon }: { label: string; value: string; onChange: (v: string) => void; options?: Opt[]; icon?: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-500 mb-1 flex items-center gap-1">{icon}{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input min-w-[11rem] max-w-[16rem]">
        <option value="">All</option>
        {(options ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
}
